import Modal from '@components/Modals/Modal.js';
import ProjectTypePicker from '@components/Projects/header/ProjectTypePicker.js';
import { route } from '@router/index.js';
import { UIDiv } from '@ui';
import {
  automatedTagsDestinationLabels,
  automatedTagsDestinations,
  getCurrentAutomatedTagsProjectType,
  ensureProjectTasks,
  ensureProjectsForAutomatedTags,
  exportAutomatedTags,
  getAutomatedTagsProjectOptions,
  getAutomatedTagsTaskOptions,
} from '../utils/automatedTagsExport.js';
import { button, clear, el } from './VisionDom.js';

const fieldClass =
  'w-full px-3 py-2 rounded-lg border border-gray-200 bg-[#F8F8FC] text-[15.625px] outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10';

function defaultTaskName(item) {
  const base = item?.name || 'Spatial Vision image';
  return `${base} automated tags`;
}

function conditionResultCount(item) {
  return (
    item?.conditionResults ||
    item?.resultMetadata?.conditionResults ||
    item?.resultMetadata?.condition_results ||
    []
  ).length;
}

function hasExportableContent(item) {
  return Boolean(
    item && ((item.detections || []).length || conditionResultCount(item)),
  );
}

function completedExportableItems(items = []) {
  return items.filter(
    item => item?.status === 'completed' && hasExportableContent(item),
  );
}

function exportStats(items = []) {
  return items.reduce(
    (stats, item) => ({
      images: stats.images + 1,
      detections: stats.detections + (item?.detections?.length || 0),
      conditions: stats.conditions + conditionResultCount(item),
      summaries: stats.summaries + (conditionResultCount(item) > 0 ? 1 : 0),
    }),
    { images: 0, detections: 0, conditions: 0, summaries: 0 },
  );
}

function exportDescription(items) {
  const stats = exportStats(items);
  const detections = stats.detections;
  const conditions = stats.conditions;
  const tagText = `${detections} positioned tag${detections === 1 ? '' : 's'}`;
  const imageText =
    stats.images > 1
      ? ` across ${stats.images} images`
      : stats.images === 1
        ? ' for 1 image'
        : '';
  if (!conditions) {
    return `${tagText}${imageText} will be created from evidence boxes.`;
  }
  return `${tagText}${imageText} and ${stats.summaries} condition summary comment${stats.summaries === 1 ? '' : 's'} will be created from ${conditions} condition result${conditions === 1 ? '' : 's'}.`;
}

function errorMessage(error) {
  if (!error) return 'Export failed';
  if (typeof error === 'string') return error;
  return error.message || error.response || 'Export failed';
}

export default class AutomatedTagsExportDialog extends Modal {
  constructor(item, items = []) {
    const batchItems = completedExportableItems(items);
    const initialScope = batchItems.length > 1 ? 'all' : 'current';
    const initialItems =
      initialScope === 'all' ? batchItems : [item].filter(Boolean);
    super('Export Automated Tags', {
      description:
        batchItems.length > 1
          ? 'Choose which completed image results to export into the same task.'
          : exportDescription(initialItems),
      showCancel: true,
      showConfirm: true,
      showCloseIcon: true,
      confirmText: 'Export',
      modalContentClass: '!max-w-2xl',
      contentContainerClass: 'px-1',
      handleOnAction: () => this.submit(),
      canClose: true,
    });
    this.item = item;
    this.items = items;
    this.batchItems = batchItems;
    this.exportScope = initialScope;
    this.mode = automatedTagsDestinations.NEW_TASK;
    this.result = null;
    this.error = null;
    this.loading = false;
    this.selectedProjectId = null;
    this.selectedTaskId = null;
    this.projectName = '';
    this.taskName = defaultTaskName(item);
    this.projectType = getCurrentAutomatedTagsProjectType();
    this.content = new UIDiv();
  }

  async show(elemId) {
    await this.loadProjectSpace();
    const modalResult = await super.show(elemId);
    return modalResult ? this.result : null;
  }

  async loadProjectSpace() {
    await ensureProjectsForAutomatedTags(this.projectType);
    const firstProject = getAutomatedTagsProjectOptions()[0] || null;
    this.selectedProjectId = firstProject?.id || null;
    if (this.selectedProjectId) {
      await ensureProjectTasks(this.selectedProjectId);
      this.selectedTaskId =
        getAutomatedTagsTaskOptions(this.selectedProjectId)[0]?.id || null;
    } else {
      this.selectedTaskId = null;
    }
  }

  createModal() {
    const modal = super.createModal();
    this.contentContainer.add(this.content);
    this.render();
    return modal;
  }

  setLoading(loading) {
    this.loading = loading;
    this.disabledActionButton(loading);
    this.cancelButton?.setDisabled(loading);
    this.actionButton?.setInnerText(loading ? 'Exporting...' : 'Export');
  }

  render() {
    clear(this.content.dom);
    const wrapper = el('div', 'space-y-4 text-left');
    if (this.batchItems.length > 1) {
      wrapper.appendChild(this.renderScopePicker());
    }
    wrapper.appendChild(this.renderProjectTypePicker());
    wrapper.appendChild(this.renderModePicker());

    if (this.mode === automatedTagsDestinations.NEW_PROJECT) {
      wrapper.appendChild(this.renderNewProjectFields());
    } else if (this.mode === automatedTagsDestinations.NEW_TASK) {
      wrapper.appendChild(this.renderNewTaskFields());
    } else {
      wrapper.appendChild(this.renderExistingTaskFields());
    }

    if (this.error) {
      wrapper.appendChild(
        el(
          'div',
          'rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[15px] text-red-700',
          { text: this.error },
        ),
      );
    }

    this.content.dom.appendChild(wrapper);
  }

  selectedExportItems() {
    if (this.exportScope === 'all') return this.batchItems;
    return [this.item].filter(Boolean);
  }

  renderScopePicker() {
    const stats = exportStats(this.selectedExportItems());
    const wrap = el(
      'div',
      'rounded-lg border border-gray-200 bg-white p-2.5 space-y-2',
    );
    wrap.appendChild(
      el('div', 'text-[13.75px] font-semibold text-[#666678]', {
        text: 'Export scope',
      }),
    );
    const group = el('div', 'grid grid-cols-2 gap-2');
    [
      ['current', 'Current image'],
      ['all', 'All completed images'],
    ].forEach(([scope, label]) => {
      const active = this.exportScope === scope;
      const option = button(
        `rounded-lg border px-3 py-2 text-left transition-colors ${
          active
            ? 'border-[#2563EB] bg-[#EEF4FF] text-[#1E3A8A]'
            : 'border-gray-200 bg-[#F8F8FC] text-[#444456] hover:bg-white'
        }`,
        '',
        () => {
          if (this.loading) return;
          this.exportScope = scope;
          this.error = null;
          this.render();
        },
      );
      option.append(
        el('div', 'text-[15px] font-semibold', { text: label }),
        el('div', 'mt-1 text-[13.125px] text-[#777788]', {
          text:
            scope === 'all'
              ? `${this.batchItems.length} completed image${this.batchItems.length === 1 ? '' : 's'}`
              : this.item?.name || 'Selected result',
        }),
      );
      group.appendChild(option);
    });
    wrap.append(
      group,
      el('p', 'text-[13.125px] leading-snug text-[#777788]', {
        text: `${stats.images} image${stats.images === 1 ? '' : 's'}, ${stats.detections} positioned tag${stats.detections === 1 ? '' : 's'}, ${stats.summaries} summary comment${stats.summaries === 1 ? '' : 's'}, ${stats.conditions} condition result${stats.conditions === 1 ? '' : 's'}`,
      }),
    );
    return wrap;
  }

  renderProjectTypePicker() {
    this.projectTypePicker?.dispose?.();
    const row = el(
      'div',
      'flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2',
    );
    row.appendChild(
      el('div', 'text-[13.75px] font-semibold text-[#666678]', {
        text: 'Project space',
      }),
    );
    this.projectTypePicker = new ProjectTypePicker({
      projectType: this.projectType,
      handleProjectTypeChange: async type => {
        if (this.loading) return 'error';
        this.projectType = type;
        this.error = null;
        await this.loadProjectSpace();
        this.render();
      },
      minifiedView: false,
    });
    row.appendChild(this.projectTypePicker.dom);
    return row;
  }

  renderModePicker() {
    const group = el('div', 'grid grid-cols-1 sm:grid-cols-3 gap-2');
    Object.values(automatedTagsDestinations).forEach(mode => {
      const active = this.mode === mode;
      const option = button(
        `min-h-[54px] rounded-lg border px-3 py-2 text-left transition-colors ${
          active
            ? 'border-[#2563EB] bg-[#EEF4FF] text-[#1E3A8A]'
            : 'border-gray-200 bg-white text-[#444456] hover:bg-[#F8F8FC]'
        }`,
        '',
        () => {
          if (this.loading) return;
          this.mode = mode;
          this.error = null;
          this.render();
        },
      );
      option.append(
        el('div', 'text-[15.625px] font-semibold', {
          text: automatedTagsDestinationLabels[mode],
        }),
        el('div', 'mt-1 text-[13.75px] leading-snug text-[#777788]', {
          text:
            mode === automatedTagsDestinations.NEW_PROJECT
              ? 'Create a project, task, file, and tags.'
              : mode === automatedTagsDestinations.NEW_TASK
                ? 'Create a task and attach the image.'
                : 'Attach the image to a task.',
        }),
      );
      group.appendChild(option);
    });
    return group;
  }

  labelledField(label, input) {
    const field = el('label', 'block space-y-1.5');
    field.append(
      el('span', 'block text-[13.75px] font-semibold text-[#666678]', {
        text: label,
      }),
      input,
    );
    return field;
  }

  input(value, onInput, placeholder = '') {
    const input = el('input', fieldClass, {
      value,
      placeholder,
      disabled: this.loading,
    });
    input.addEventListener('input', () => {
      onInput(input.value);
      this.error = null;
    });
    return input;
  }

  projectSelect(onChange) {
    const select = el('select', fieldClass, { disabled: this.loading });
    const projects = getAutomatedTagsProjectOptions();
    if (!projects.length) {
      select.appendChild(el('option', '', { text: 'No projects available' }));
      select.disabled = true;
      return select;
    }
    projects.forEach(project => {
      select.appendChild(
        el('option', '', {
          value: project.id,
          text: project.name || 'Untitled project',
          selected: String(project.id) === String(this.selectedProjectId),
        }),
      );
    });
    select.addEventListener('change', async () => {
      this.selectedProjectId = select.value;
      this.error = null;
      await ensureProjectTasks(this.selectedProjectId);
      this.selectedTaskId =
        getAutomatedTagsTaskOptions(this.selectedProjectId)[0]?.id || null;
      onChange?.();
      this.render();
    });
    return select;
  }

  taskSelect() {
    const select = el('select', fieldClass, { disabled: this.loading });
    const tasks = getAutomatedTagsTaskOptions(this.selectedProjectId);
    if (!tasks.length) {
      select.appendChild(
        el('option', '', { text: 'No tasks in this project' }),
      );
      select.disabled = true;
      return select;
    }
    tasks.forEach(task => {
      select.appendChild(
        el('option', '', {
          value: task.id,
          text: task.name || 'Untitled task',
          selected: String(task.id) === String(this.selectedTaskId),
        }),
      );
    });
    select.addEventListener('change', () => {
      this.selectedTaskId = select.value;
      this.error = null;
    });
    return select;
  }

  renderNewProjectFields() {
    const fields = el('div', 'grid grid-cols-1 gap-3');
    fields.append(
      this.labelledField(
        'Project name',
        this.input(
          this.projectName,
          value => {
            this.projectName = value;
          },
          'New project name',
        ),
      ),
      this.labelledField(
        'Task name',
        this.input(
          this.taskName,
          value => {
            this.taskName = value;
          },
          'Task name',
        ),
      ),
    );
    return fields;
  }

  renderNewTaskFields() {
    const fields = el('div', 'grid grid-cols-1 gap-3');
    fields.append(
      this.labelledField('Project', this.projectSelect()),
      this.labelledField(
        'Task name',
        this.input(
          this.taskName,
          value => {
            this.taskName = value;
          },
          'Task name',
        ),
      ),
    );
    return fields;
  }

  renderExistingTaskFields() {
    const fields = el('div', 'grid grid-cols-1 gap-3');
    fields.append(
      this.labelledField('Project', this.projectSelect()),
      this.labelledField('Task', this.taskSelect()),
    );
    return fields;
  }

  destination() {
    const taskName = this.taskName.trim();
    if (this.mode === automatedTagsDestinations.NEW_PROJECT) {
      const projectName = this.projectName.trim();
      if (!projectName) throw new Error('Project name is required');
      if (!taskName) throw new Error('Task name is required');
      return { mode: this.mode, projectName, taskName };
    }
    if (this.mode === automatedTagsDestinations.NEW_TASK) {
      if (!this.selectedProjectId) throw new Error('Select a project');
      if (!taskName) throw new Error('Task name is required');
      return {
        mode: this.mode,
        projectId: this.selectedProjectId,
        taskName,
      };
    }
    if (!this.selectedTaskId) throw new Error('Select a task');
    return {
      mode: this.mode,
      projectId: this.selectedProjectId,
      taskId: this.selectedTaskId,
    };
  }

  async submit() {
    try {
      this.error = null;
      this.setLoading(true);
      const selectedItems = this.selectedExportItems();
      this.result = await exportAutomatedTags({
        item: this.item,
        items: this.exportScope === 'all' ? selectedItems : undefined,
        destination: this.destination(),
      });
      setTimeout(() => showAutomatedTagsSuccess(this.result), 350);
      return true;
    } catch (error) {
      this.error = errorMessage(error);
      this.setLoading(false);
      this.render();
      return false;
    }
  }
}

export async function showAutomatedTagsExportDialog(item, items = []) {
  const dialog = new AutomatedTagsExportDialog(item, items);
  return dialog.show();
}

export async function showAutomatedTagsSuccess(result) {
  const created = result?.createdCount || 0;
  const failed = result?.failedCount || 0;
  const tagFailed = result?.tagFailedCount || 0;
  const imageFailed = result?.imageFailedCount || 0;
  const summaryFailed = result?.summaryFailedCount || 0;
  const images = result?.exportedImageCount || 0;
  const imageTotal = result?.imageCount || 0;
  const summaries = result?.summaryCreatedCount || 0;
  const summaryText = result?.conditionResultCount
    ? ` ${summaries} condition summary comment${summaries === 1 ? '' : 's'} created.`
    : '';
  const failureDetails = [
    imageFailed
      ? `${imageFailed} image${imageFailed === 1 ? '' : 's'} failed`
      : null,
    tagFailed
      ? `${tagFailed} positioned tag${tagFailed === 1 ? '' : 's'} failed`
      : null,
    summaryFailed
      ? `${summaryFailed} summary comment${summaryFailed === 1 ? '' : 's'} failed`
      : null,
  ]
    .filter(Boolean)
    .join(', ');
  const description = failed
    ? `${images} of ${imageTotal} image${imageTotal === 1 ? '' : 's'} exported. ${created} of ${result.totalCount} positioned tags were created. ${failureDetails || `${failed} export item${failed === 1 ? '' : 's'} failed`}.${summaryText}`
    : `${images} image${images === 1 ? '' : 's'} exported to "${result?.task?.name || 'the task'}". ${created} positioned tag${created === 1 ? '' : 's'} created.${summaryText}`;
  const modal = new Modal('Automated Tags Exported', {
    description,
    showCancel: true,
    showConfirm: Boolean(result?.viewerUrl),
    showCloseIcon: true,
    cancelText: 'Stay here',
    confirmText: 'Open viewer',
    handleOnAction: () => {
      if (result?.viewerUrl) route(result.viewerUrl);
      return true;
    },
  });
  await modal.show();
}
